/**
 * Sequences management page
 *
 * Visual interface for creating and editing nurture sequence definitions.
 */

import { useState } from 'react';
import { toast } from 'sonner';
import {
  Plus,
  Mail,
  MessageSquare,
  Clock,
  Trash2,
  Edit2,
  ChevronDown,
  ChevronUp,
  Power,
  PowerOff,
  GripVertical,
  Save,
  X,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useSequenceDefinitions,
  useSequenceDefinition,
  useCreateSequenceDefinition,
  useUpdateSequenceDefinition,
  useDeleteSequenceDefinition,
} from '@/lib/api/hooks/useSequences';
import type { SequenceStep, SequenceDefinitionListItem } from '@/types/sequence';
import { SEQUENCE_TRIGGERS, TEMPLATE_VARIABLES } from '@/types/sequence';

// Step editor component
interface StepEditorProps {
  step: Partial<SequenceStep>;
  index: number;
  onChange: (index: number, step: Partial<SequenceStep>) => void;
  onRemove: (index: number) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}

function StepEditor({
  step,
  index,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
}: StepEditorProps) {
  const [expanded, setExpanded] = useState(true);

  const handleChannelChange = (channel: 'sms' | 'email') => {
    onChange(index, { ...step, channel });
  };

  const handleDelayChange = (value: string) => {
    const delay = parseInt(value, 10);
    if (!isNaN(delay) && delay >= 0) {
      onChange(index, { ...step, delay_minutes: delay });
    }
  };

  const handleTemplateContentChange = (field: string, value: string) => {
    onChange(index, {
      ...step,
      template_content: {
        ...step.template_content,
        [field]: value,
      },
    });
  };

  return (
    <Card className="border-l-4 border-l-primary/50">
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex flex-col gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={onMoveUp}
                disabled={!canMoveUp}
              >
                <ChevronUp className="h-3 w-3" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-5 w-5"
                onClick={onMoveDown}
                disabled={!canMoveDown}
              >
                <ChevronDown className="h-3 w-3" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono">
                Step {index + 1}
              </Badge>
              {step.channel === 'sms' ? (
                <Badge variant="success">
                  <MessageSquare className="h-3 w-3 mr-1" />
                  SMS
                </Badge>
              ) : (
                <Badge variant="info">
                  <Mail className="h-3 w-3 mr-1" />
                  Email
                </Badge>
              )}
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {step.delay_minutes} min delay
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => onRemove(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 pb-4 px-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Channel</Label>
              <Select value={step.channel} onValueChange={(v) => handleChannelChange(v as 'sms' | 'email')}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sms">
                    <span className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      SMS
                    </span>
                  </SelectItem>
                  <SelectItem value="email">
                    <span className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Delay (minutes)</Label>
              <Input
                type="number"
                min="0"
                value={step.delay_minutes || 0}
                onChange={(e) => handleDelayChange(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>

          {step.channel === 'sms' ? (
            <div>
              <Label>SMS Message</Label>
              <Textarea
                value={step.template_content?.sms_text || ''}
                onChange={(e) => handleTemplateContentChange('sms_text', e.target.value)}
                placeholder="Hi {name}, this is Jason from BuildGravity..."
                className="mt-1 min-h-[100px] font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Max 160 chars for single SMS. Variables: {TEMPLATE_VARIABLES.map(v => v.variable).join(', ')}
              </p>
            </div>
          ) : (
            <>
              <div>
                <Label>Email Subject</Label>
                <Input
                  value={step.template_content?.email_subject || ''}
                  onChange={(e) => handleTemplateContentChange('email_subject', e.target.value)}
                  placeholder="Quick question about your practice"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email Body</Label>
                <Textarea
                  value={step.template_content?.email_body || ''}
                  onChange={(e) => handleTemplateContentChange('email_body', e.target.value)}
                  placeholder="Hi {name},&#10;&#10;I noticed you were interested in BuildGravity..."
                  className="mt-1 min-h-[150px] font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Variables: {TEMPLATE_VARIABLES.map(v => v.variable).join(', ')}
                </p>
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// Sequence editor dialog
interface SequenceEditorProps {
  open: boolean;
  onClose: () => void;
  editingId?: string;
}

function SequenceEditor({ open, onClose, editingId }: SequenceEditorProps) {
  const { data: existingSequence, isLoading: loadingExisting } = useSequenceDefinition(editingId || '');
  const createMutation = useCreateSequenceDefinition();
  const updateMutation = useUpdateSequenceDefinition();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [trigger, setTrigger] = useState<string>('manual');
  const [active, setActive] = useState(true);
  const [steps, setSteps] = useState<Partial<SequenceStep>[]>([
    { channel: 'sms', delay_minutes: 5, template_content: { sms_text: '' } },
  ]);

  // Load existing data when editing
  useState(() => {
    if (existingSequence && editingId) {
      setName(existingSequence.name);
      setDescription(existingSequence.description || '');
      setTrigger(existingSequence.trigger || 'manual');
      setActive(existingSequence.active);
      setSteps(existingSequence.steps);
    }
  });

  // Reset form when dialog opens/closes
  const resetForm = () => {
    if (existingSequence && editingId) {
      setName(existingSequence.name);
      setDescription(existingSequence.description || '');
      setTrigger(existingSequence.trigger || 'manual');
      setActive(existingSequence.active);
      setSteps(existingSequence.steps);
    } else {
      setName('');
      setDescription('');
      setTrigger('manual');
      setActive(true);
      setSteps([{ channel: 'sms', delay_minutes: 5, template_content: { sms_text: '' } }]);
    }
  };

  // Update form when existing sequence loads
  if (existingSequence && editingId && name === '' && existingSequence.name) {
    setName(existingSequence.name);
    setDescription(existingSequence.description || '');
    setTrigger(existingSequence.trigger || 'manual');
    setActive(existingSequence.active);
    setSteps(existingSequence.steps);
  }

  const handleStepChange = (index: number, step: Partial<SequenceStep>) => {
    const newSteps = [...steps];
    newSteps[index] = step;
    setSteps(newSteps);
  };

  const handleRemoveStep = (index: number) => {
    if (steps.length > 1) {
      setSteps(steps.filter((_, i) => i !== index));
    }
  };

  const handleAddStep = () => {
    setSteps([
      ...steps,
      { channel: 'sms', delay_minutes: 15, template_content: { sms_text: '' } },
    ]);
  };

  const handleMoveStep = (index: number, direction: 'up' | 'down') => {
    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
    setSteps(newSteps);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a sequence name');
      return;
    }

    if (steps.length === 0) {
      toast.error('Please add at least one step');
      return;
    }

    // Validate steps have content
    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (step.channel === 'sms' && !step.template_content?.sms_text?.trim()) {
        toast.error(`Step ${i + 1}: Please enter SMS message`);
        return;
      }
      if (step.channel === 'email') {
        if (!step.template_content?.email_subject?.trim()) {
          toast.error(`Step ${i + 1}: Please enter email subject`);
          return;
        }
        if (!step.template_content?.email_body?.trim()) {
          toast.error(`Step ${i + 1}: Please enter email body`);
          return;
        }
      }
    }

    try {
      if (editingId) {
        await updateMutation.mutateAsync({
          id: editingId,
          data: {
            name,
            description: description || undefined,
            trigger: trigger !== 'manual' ? trigger : undefined,
            active,
            steps: steps.map((s, i) => ({
              step_number: i + 1,
              delay_minutes: s.delay_minutes || 0,
              channel: s.channel || 'sms',
              template_name: 'custom',
              template_content: s.template_content,
            })) as SequenceStep[],
          },
        });
        toast.success('Sequence updated');
      } else {
        await createMutation.mutateAsync({
          name,
          description: description || undefined,
          trigger: trigger !== 'manual' ? trigger : undefined,
          active,
          steps: steps.map((s) => ({
            delay_minutes: s.delay_minutes || 0,
            channel: s.channel || 'sms',
            template_name: 'custom',
            template_content: s.template_content,
          })),
        });
        toast.success('Sequence created');
      }
      onClose();
      resetForm();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save sequence');
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingId ? 'Edit Sequence' : 'Create Sequence'}</DialogTitle>
          <DialogDescription>
            Define the steps and timing for your nurture sequence.
          </DialogDescription>
        </DialogHeader>

        {loadingExisting && editingId ? (
          <div className="space-y-4 py-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Sequence Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Meta Lead Follow-up"
                  className="mt-1"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="description">Description (optional)</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this sequence for?"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Auto-Start Trigger</Label>
                <Select value={trigger} onValueChange={setTrigger}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEQUENCE_TRIGGERS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  variant={active ? 'default' : 'outline'}
                  onClick={() => setActive(!active)}
                  className="w-full"
                >
                  {active ? (
                    <>
                      <Power className="h-4 w-4 mr-2" />
                      Active
                    </>
                  ) : (
                    <>
                      <PowerOff className="h-4 w-4 mr-2" />
                      Inactive
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Steps */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-base">Sequence Steps</Label>
                <Button variant="outline" size="sm" onClick={handleAddStep}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Step
                </Button>
              </div>
              <div className="space-y-3">
                {steps.map((step, index) => (
                  <StepEditor
                    key={index}
                    step={step}
                    index={index}
                    onChange={handleStepChange}
                    onRemove={handleRemoveStep}
                    onMoveUp={() => handleMoveStep(index, 'up')}
                    onMoveDown={() => handleMoveStep(index, 'down')}
                    canMoveUp={index > 0}
                    canMoveDown={index < steps.length - 1}
                  />
                ))}
              </div>
            </div>

            {/* Template Variables Reference */}
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm font-medium mb-2">Available Variables</p>
              <div className="flex flex-wrap gap-2">
                {TEMPLATE_VARIABLES.map((v) => (
                  <Badge key={v.variable} variant="secondary" className="font-mono text-xs">
                    {v.variable}
                    <span className="ml-1 font-normal text-muted-foreground">- {v.description}</span>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {editingId ? 'Update Sequence' : 'Create Sequence'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main page component
export function SequencesPage() {
  const { data, isLoading, error, refetch } = useSequenceDefinitions();
  const deleteMutation = useDeleteSequenceDefinition();
  const updateMutation = useUpdateSequenceDefinition();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | undefined>();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleCreate = () => {
    setEditingId(undefined);
    setEditorOpen(true);
  };

  const handleEdit = (id: string) => {
    setEditingId(id);
    setEditorOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success('Sequence deleted');
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete sequence');
    }
  };

  const handleToggleActive = async (item: SequenceDefinitionListItem) => {
    try {
      await updateMutation.mutateAsync({
        id: item.id,
        data: { active: !item.active },
      });
      toast.success(item.active ? 'Sequence deactivated' : 'Sequence activated');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update sequence');
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold tracking-tight">Nurture Sequences</h1>
            <p className="text-sm text-muted-foreground">
              Automated SMS and email follow-up sequences
            </p>
          </div>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            New Sequence
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="space-y-4 max-w-3xl">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-48" />
                  <Skeleton className="h-4 w-64" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <Card className="p-8 text-center max-w-md mx-auto">
            <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
            <h3 className="text-lg font-medium mb-2">Failed to Load Sequences</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {error instanceof Error ? error.message : 'An error occurred'}
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </Card>
        ) : !data?.definitions.length ? (
          <Card className="p-8 text-center max-w-md mx-auto">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Sequences Yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first nurture sequence to automatically follow up with leads.
            </p>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Sequence
            </Button>
          </Card>
        ) : (
          <div className="space-y-4 max-w-3xl">
            {data.definitions.map((item) => (
              <Card key={item.id} className={!item.active ? 'opacity-60' : ''}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {item.name}
                        {item.active ? (
                          <Badge variant="success">Active</Badge>
                        ) : (
                          <Badge variant="neutral">Inactive</Badge>
                        )}
                      </CardTitle>
                      {item.description && (
                        <CardDescription>{item.description}</CardDescription>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleActive(item)}
                        title={item.active ? 'Deactivate' : 'Activate'}
                      >
                        {item.active ? (
                          <Power className="h-4 w-4 text-green-600" />
                        ) : (
                          <PowerOff className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(item.id)}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => setDeleteConfirm(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {item.steps_count} step{item.steps_count !== 1 ? 's' : ''}
                    </span>
                    {item.trigger && (
                      <Badge variant="outline">
                        Trigger: {SEQUENCE_TRIGGERS.find(t => t.value === item.trigger)?.label || item.trigger}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Editor Dialog */}
      <SequenceEditor
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditingId(undefined);
        }}
        editingId={editingId}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sequence?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this sequence definition. Running sequences won't be affected,
              but no new leads will be enrolled in this sequence.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
